import ExcelJS from "exceljs";

/** MarginalityReport / reportify header green (ARGB). */
export const HEADER_FILL = "FF72BF44";
/** Reportify thin border color (ARGB). */
export const BORDER_COLOR = "FFAAAAAA";
export const FONT_NAME = "Verdana";
export const FONT_SIZE = 8;
export const DATE_COL_WIDTH = 16;
export const METRIC_COL_WIDTH = 15;

const thinBorder: Partial<ExcelJS.Borders> = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

const isDebug =
  typeof process !== "undefined" &&
  process.env &&
  (process.env.NODE_ENV === "development" || process.env.LOG_LEVEL === "debug");

function debugLog(message: string, data?: Record<string, unknown>) {
  if (!isDebug) return;
  if (data) {
    console.debug(`[buildTransportCostsWorkbook] ${message}`, data);
  } else {
    console.debug(`[buildTransportCostsWorkbook] ${message}`);
  }
}

/**
 * Build a styled workbook for TransportCostsReport matrix export.
 * AOA: row 0 = headers, following rows = date + numeric metrics.
 */
export function buildTransportCostsWorkbook(
  rows: any[][],
  sheetName = "Отчет"
): ExcelJS.Workbook {
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const colCount =
    rowCount > 0 && Array.isArray(rows[0]) ? rows[0].length : 0;

  debugLog("START", { rowCount, colCount, sheetName });

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  if (!rowCount || !colCount) {
    debugLog("empty matrix — returning empty sheet");
    return workbook;
  }

  rows.forEach((rowValues, rowIndex) => {
    const excelRow = worksheet.addRow(rowValues);
    excelRow.height = Math.max(22, FONT_SIZE + 10);

    excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = {
        name: FONT_NAME,
        size: FONT_SIZE,
        bold: rowIndex === 0,
      };
      cell.border = thinBorder as ExcelJS.Borders;

      if (rowIndex === 0) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: HEADER_FILL },
        };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      } else if (colNumber >= 2) {
        cell.numFmt = "0.00";
        cell.alignment = { vertical: "middle", horizontal: "center" };
        if (typeof cell.value === "string" && cell.value !== "") {
          const n = parseFloat(cell.value);
          if (Number.isFinite(n)) {
            cell.value = n;
          }
        }
      } else {
        cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      }
    });
  });

  worksheet.getColumn(1).width = DATE_COL_WIDTH;
  for (let i = 2; i <= colCount; i++) {
    worksheet.getColumn(i).width = METRIC_COL_WIDTH;
  }

  debugLog("DONE", {
    rowCount,
    colCount,
    dateColWidth: DATE_COL_WIDTH,
    metricColWidth: METRIC_COL_WIDTH,
  });

  return workbook;
}

export async function writeTransportCostsWorkbookBuffer(
  rows: any[][]
): Promise<ExcelJS.Buffer> {
  try {
    const workbook = buildTransportCostsWorkbook(rows);
    return await workbook.xlsx.writeBuffer();
  } catch (error) {
    const e = error as any;
    console.error("[buildTransportCostsWorkbook] writeBuffer ERROR", {
      message: e && e.message,
      stack: e && e.stack,
      rowCount: Array.isArray(rows) ? rows.length : 0,
    });
    throw error;
  }
}
